import React, { useEffect } from 'react';
import { ContactForm } from './Contact';
import Header from './Header';
import pizzaListImage from '../assets/pizza-list.png';
import pizzaMapImage from '../assets/pizza-map.png';
import './AppsSdkMcpLP.css';

export default function AppsSdkMcpLP() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'ChatGPTアプリ連携 | サービスの検索・操作を会話から';
    return () => { document.title = previousTitle; };
  }, []);

  return (
    <div className="home apps-page">
      <Header />
      <main className="apps-main">
        <section className="apps-hero" aria-labelledby="apps-title">
          <div className="apps-wrap apps-hero-grid">
            <div>
              <p className="apps-eyebrow">ChatGPTアプリ連携</p>
              <h1 id="apps-title">あなたのサービスを、<br />会話から使えるように。</h1>
              <p className="apps-lead">商品を探す、情報を確認する、次の操作に進む。<br />既存サービスの機能をChatGPTにつなぎ、会話の中で使える画面と仕組みを開発します。</p>
              <div className="apps-actions"><a className="apps-button" href="#apps-contact">連携について相談する <span aria-hidden="true">↗</span></a><a className="apps-text-link" href="#apps-demo">画面の紹介例を見る ↓</a></div>
              <p className="apps-note">既存サービスとの接続から、認証・権限・運用までご相談いただけます。</p>
            </div>
            <div className="apps-concept" aria-label="会話から既存サービスの情報を確認する連携の説明図">
              <p className="apps-concept-label">連携のイメージ</p>
              <p className="apps-conversation">条件に合う商品を探して</p>
              <div className="apps-connection"><span>ChatGPT</span><span aria-hidden="true">↕</span><span>あなたのサービス</span></div>
              <div className="apps-concept-result"><span aria-hidden="true">▤</span><div><strong>結果を、見て選べる画面に</strong><p>一覧・詳細・操作の導線を表示</p></div></div>
              <p className="apps-note">説明用のイメージです。</p>
            </div>
          </div>
        </section>

        <section aria-labelledby="apps-features-title">
          <div className="apps-wrap">
            <p className="apps-eyebrow">できること</p><h2 id="apps-features-title">探すところから、<br />サービスを使うところまで。</h2>
            <div className="apps-three-columns">
              <article><span className="apps-number">01</span><h3>条件を伝えて検索</h3><p>商品・店舗・社内情報などを、会話で伝えた条件に合わせて検索できるようにします。</p></article>
              <article><span className="apps-number">02</span><h3>結果を見ながら比較</h3><p>一覧や地図、詳細画面で情報を表示。候補を見比べ、必要な情報を確かめられます。</p></article>
              <article><span className="apps-number">03</span><h3>確認して操作に進む</h3><p>既存サービスの機能に合わせて、情報の更新や手続きの導線を設計します。利用者の権限や確認手順も整えます。</p></article>
            </div>
            <p className="apps-note">連携できる範囲は、既存API・データ・利用条件を確認して決めます。</p>
          </div>
        </section>

        <section id="apps-demo" className="apps-tinted" aria-labelledby="apps-demo-title">
          <div className="apps-wrap">
            <p className="apps-eyebrow">画面の紹介例</p><h2 id="apps-demo-title">会話の結果を、リストや地図で。</h2>
            <p className="apps-section-lead">レストラン検索を題材にしたサンプル画面です。サービスの内容に合わせて、情報の見せ方と操作を設計します。</p>
            <div className="apps-demo-grid">
              <figure><div className="apps-demo-image"><img src={pizzaListImage} alt="ピザレストランの候補を一覧で表示するサンプル画面" loading="lazy" /></div><figcaption><h3>候補を一覧で確認</h3><p>複数の検索結果を並べて、気になる候補を選べる表示例です。</p></figcaption></figure>
              <figure><div className="apps-demo-image"><img src={pizzaMapImage} alt="ピザレストランの位置を地図で表示するサンプル画面" loading="lazy" /></div><figcaption><h3>位置を地図で確認</h3><p>場所と候補の情報を照らし合わせながら、比較できる表示例です。</p></figcaption></figure>
            </div>
            <p className="apps-note">開発者モードのサンプル画像です。お客様への導入実績を示すものではありません。</p>
          </div>
        </section>

        <section aria-labelledby="apps-scope-title">
          <div className="apps-wrap apps-split">
            <div><p className="apps-eyebrow">支援範囲</p><h2 id="apps-scope-title">つなぐ仕組みと、<br />使い続ける運用を。</h2><p className="apps-section-lead">MCPは、ChatGPTと外部サービスの機能をつなぐための共通の仕組みです。必要な機能から接続し、画面と運用を整えます。</p></div>
            <div className="apps-scope-list">
              <article><h3>既存サービスとの接続</h3><p>APIやデータの構成を確認し、MCPサーバーと検索・操作の機能を開発します。</p></article>
              <article><h3>会話の中で使う画面</h3><p>結果の一覧や詳細、操作ボタンを実装。読み込み中やエラー時の案内も設計します。</p></article>
              <article><h3>認証・権限の設計</h3><p>アカウント連携、閲覧・操作の権限、データの取り扱いを整理します。</p></article>
              <article><h3>運用・監視の整備</h3><p>ログ、異常の検知、変更時の確認手順を用意し、公開後の保守範囲を決めます。</p></article>
            </div>
          </div>
        </section>

        <section className="apps-tinted" aria-labelledby="apps-process-title">
          <div className="apps-wrap">
            <p className="apps-eyebrow">進め方</p><h2 id="apps-process-title">まずは、つなぎたい機能を一つ。</h2>
            <ol className="apps-three-columns apps-process">
              <li><span className="apps-number">01 / ご相談</span><h3>用途と対象を整理</h3><p>誰が、どんな場面で使うかを伺い、連携するサービスと機能を絞ります。</p></li>
              <li><span className="apps-number">02 / 設計・試作</span><h3>接続と操作を確かめる</h3><p>APIや権限を確認し、試作で検索結果や操作の流れを確かめます。</p></li>
              <li><span className="apps-number">03 / 開発・運用</span><h3>使う環境へ整える</h3><p>合意した範囲を開発し、必要な公開手続きと運用準備を進めます。</p></li>
            </ol>
            <p className="apps-note">費用・期間は連携先と開発範囲を確認してお見積りします。公開方法や条件は、その時点の公式要件に沿って確認します。</p>
          </div>
        </section>

        <section className="apps-reference" aria-labelledby="apps-reference-title"><div className="apps-wrap"><h2 id="apps-reference-title">技術について詳しく知りたい方へ</h2><p>OpenAIの公式開発者資料で、MCP接続と画面の実装方法をご確認いただけます。</p><div className="apps-reference-links"><a href="https://developers.openai.com/plugins/build/mcp-server">MCPサーバーの構築 ↗</a><a href="https://developers.openai.com/plugins/build/chatgpt-ui">MCPサーバーへの画面追加 ↗</a></div></div></section>

        <section id="apps-contact" className="apps-contact" aria-labelledby="apps-contact-title">
          <div className="apps-wrap apps-contact-wrap"><p className="apps-eyebrow">お問い合わせ</p><h2 id="apps-contact-title">どの機能をChatGPTにつなげたいですか？</h2><p className="apps-section-lead">サービスの概要と、実現したい使い方をお聞かせください。仕様が固まっていない段階でもご相談いただけます。</p><ContactForm showTitle={false} /></div>
        </section>
      </main>
    </div>
  );
}
